import { useEffect, useState } from "react";
import { Truck, Plus, Pencil, Trash2, X, Save, Loader2, User, Package, Ruler, Weight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const empty = { name: "", plate: "", surface_m2: "", height_m: "", payload_kg: "", cost_day: "", cost_hour: "", cost_km: "", driver_name: "", depot: "", active: true };

export default function Fleet() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // "new" | id
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/vehicles").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(empty); setEditing("new"); };
  const startEdit = (v) => { setForm({ ...empty, ...v }); setEditing(v.id); };
  const cancel = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom du véhicule requis"); return; }
    setSaving(true);
    const payload = { name: form.name, plate: form.plate, driver_name: form.driver_name, depot: form.depot, active: form.active,
      surface_m2: Number(form.surface_m2 || 0), height_m: Number(form.height_m || 0), payload_kg: Number(form.payload_kg || 0),
      cost_day: Number(form.cost_day || 0), cost_hour: Number(form.cost_hour || 0), cost_km: Number(form.cost_km || 0) };
    try {
      if (editing === "new") await api.post("/vehicles", payload);
      else await api.put(`/vehicles/${editing}`, payload);
      toast.success("Véhicule enregistré"); cancel(); load();
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce véhicule ?")) return;
    await api.delete(`/vehicles/${id}`); toast.success("Véhicule supprimé"); load();
  };

  const input = "w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30";

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display text-4xl text-forest flex items-center gap-2"><Truck className="text-brand" /> Flotte</h1>
        <button onClick={startNew} data-testid="vehicle-new-btn" className="rounded-full bg-brand text-cream px-4 py-2 text-sm font-semibold hover:bg-forest flex items-center gap-1.5"><Plus size={16} /> Ajouter un véhicule</button>
      </div>
      <p className="text-stone mb-6">Véhicules de location : surface au sol, hauteur et charge utile → capacité de chargement</p>

      {rows.length === 0 ? (
        <div className="rounded-xl bg-white border border-border p-10 text-center text-stone">Aucun véhicule. Ajoutez vos véhicules de location pour calculer le taux de remplissage.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((v) => (
            <div key={v.id} className="rounded-xl bg-white border border-border p-5" data-testid={`vehicle-${v.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-forest">{v.name}</p>
                  {v.plate && <p className="text-xs text-stone font-mono">{v.plate}</p>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(v)} data-testid={`vehicle-edit-${v.id}`} className="text-stone hover:text-brand p-1.5"><Pencil size={15} /></button>
                  <button onClick={() => remove(v.id)} data-testid={`vehicle-delete-${v.id}`} className="text-red-500 hover:bg-red-50 rounded p-1.5"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-[#F0EFEA] p-2"><p className="font-display text-lg text-forest">{v.payload_kg}</p><p className="text-[10px] text-stone flex items-center justify-center gap-0.5"><Weight size={10} /> kg</p></div>
                <div className="rounded-lg bg-[#F0EFEA] p-2"><p className="font-display text-lg text-forest">{v.volume_m3}</p><p className="text-[10px] text-stone flex items-center justify-center gap-0.5"><Package size={10} /> m³</p></div>
                <div className="rounded-lg bg-[#F0EFEA] p-2"><p className="font-display text-lg text-forest">{v.surface_m2}</p><p className="text-[10px] text-stone flex items-center justify-center gap-0.5"><Ruler size={10} /> m²</p></div>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-stone">
                {v.driver_name && <span className="flex items-center gap-1"><User size={12} /> {v.driver_name}</span>}
                {v.height_m > 0 && <span>Hauteur {v.height_m} m</span>}
                {v.cost_day > 0 && <span>{v.cost_day} €/jour</span>}
                {v.depot && <span>Dépôt {v.depot}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={cancel} data-testid="vehicle-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="font-semibold text-forest">{editing === "new" ? "Nouveau véhicule" : "Modifier le véhicule"}</h3>
              <button onClick={cancel} className="p-2 rounded-lg hover:bg-sage/50"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <input className={input} placeholder="Nom (ex: Camion benne 3.5T)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vehicle-name" />
                <input className={input} placeholder="Immatriculation" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} data-testid="vehicle-plate" />
              </div>
              <p className="text-xs font-bold text-forest pt-1">Capacité de chargement</p>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-xs text-stone">Surface (m²)</label><input type="number" step="0.1" className={input} value={form.surface_m2} onChange={(e) => setForm({ ...form, surface_m2: e.target.value })} data-testid="vehicle-surface" /></div>
                <div><label className="text-xs text-stone">Hauteur (m)</label><input type="number" step="0.1" className={input} value={form.height_m} onChange={(e) => setForm({ ...form, height_m: e.target.value })} data-testid="vehicle-height" /></div>
                <div><label className="text-xs text-stone">Charge utile (kg)</label><input type="number" step="10" className={input} value={form.payload_kg} onChange={(e) => setForm({ ...form, payload_kg: e.target.value })} data-testid="vehicle-payload" /></div>
              </div>
              <p className="text-xs text-stone">Volume utile calculé : <strong>{(Number(form.surface_m2 || 0) * Number(form.height_m || 0)).toFixed(2)} m³</strong></p>
              <p className="text-xs font-bold text-forest pt-1">Coûts & chauffeur</p>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-xs text-stone">€/jour</label><input type="number" className={input} value={form.cost_day} onChange={(e) => setForm({ ...form, cost_day: e.target.value })} /></div>
                <div><label className="text-xs text-stone">€/heure</label><input type="number" className={input} value={form.cost_hour} onChange={(e) => setForm({ ...form, cost_hour: e.target.value })} /></div>
                <div><label className="text-xs text-stone">€/km</label><input type="number" step="0.01" className={input} value={form.cost_km} onChange={(e) => setForm({ ...form, cost_km: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input className={input} placeholder="Chauffeur" value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} data-testid="vehicle-driver" />
                <input className={input} placeholder="Dépôt (Saint-Victoret / Cabriès)" value={form.depot} onChange={(e) => setForm({ ...form, depot: e.target.value })} />
              </div>
              <button onClick={save} disabled={saving} data-testid="vehicle-save" className="w-full rounded-full bg-brand text-cream py-3 font-semibold hover:bg-forest flex items-center justify-center gap-2 disabled:opacity-60">{saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
